/**
 * The portfolio's first-load loader UI, on top of the forming ring: the
 * numbered counter on REAL progress, bottom right (the scroll rail's
 * corner, which it hands over to as the loader clears). The counter
 * paces itself to the ring: when loading runs ahead, it lands on 100 as
 * the last card lands, and the ring waits for it before it unrolls.
 * Escape and Tab
 * skip the entrance. White in difference blend, so it is dark on the
 * loader's off-white and stays legible as the backdrop crossfades to the
 * dark page. The nav's own name, top left, is the brand word (see
 * Nav.tsx), so nothing here duplicates it.
 *
 * Mounted by PageTransition for the loader phases; fades out as the ring
 * starts unrolling ("expanding") and unmounts once faded.
 */
import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import NumberedLoader from "./NumberedLoader";
import { animationConfig } from "../../config/animation";
import {
    entranceFormationAtom,
    entrancePhaseAtom,
    entranceProgressAtom,
    isLoaderPhase,
    markEntranceCountDone,
    requestEntranceSkip,
} from "../../state/entrance";

const ENTRANCE = animationConfig.portfolio.entrance;
const FADE = `opacity ${ENTRANCE.loaderFadeOut.durationMs}ms ${ENTRANCE.loaderFadeOut.easing}`;

export default function EntranceLoader() {
    const phase = useAtomValue(entrancePhaseAtom);
    const progress = useAtomValue(entranceProgressAtom);
    const formation = useAtomValue(entranceFormationAtom);
    const fading = phase !== "boot" && phase !== "loading";
    // Stay mounted through the fade, then go.
    const [gone, setGone] = useState(false);
    useEffect(() => {
        if (!fading) return;
        const id = window.setTimeout(() => setGone(true), ENTRANCE.loaderFadeOut.durationMs);
        return () => window.clearTimeout(id);
    }, [fading]);

    // Escape and Tab end the entrance instantly.
    useEffect(() => {
        if (fading) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape" && e.key !== "Tab") return;
            e.preventDefault();
            requestEntranceSkip();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [fading]);

    if (gone || (!isLoaderPhase(phase) && !fading)) return null;

    return (
        <div
            className="stage z-[35] pointer-events-none text-white mix-blend-difference text-ui font-[400] sm:font-[300] tracking-wide"
            style={{ opacity: fading ? 0 : 1, transition: FADE }}
        >
            <div className="absolute bottom-0 right-0 px-6 py-4 md:p-[3em]">
                <NumberedLoader
                    progress={progress}
                    landAt={formation ? { startMs: formation.startMs, atMs: formation.endMs } : undefined}
                    onCountComplete={markEntranceCountDone}
                    className="relative"
                />
            </div>
        </div>
    );
}
